/**
 * Maps legacy fieldConfig.behavior → scene graph layout vocabulary.
 * Preserves fieldKey / fieldConfig on nodes for API compatibility.
 */

export const LAYOUT_MODES = [
  'fixedFrame',
  'autoHeight',
  'autoWidth',
  'scaleDown',
  'truncate',
  'adaptiveSpacing',
];

export const OVERFLOW_STRATEGIES = ['clip', 'ellipsis', 'scale', 'wrap'];

const LEGACY_BEHAVIOR_MAP = {
  none: { layoutMode: 'fixedFrame', overflowStrategy: 'clip' },
  wrap: { layoutMode: 'autoHeight', overflowStrategy: 'wrap' },
  fitBox: { layoutMode: 'autoHeight', overflowStrategy: 'wrap' },
  shrink: { layoutMode: 'scaleDown', overflowStrategy: 'scale' },
  both: { layoutMode: 'scaleDown', overflowStrategy: 'scale' },
};

/** @param {Record<string, unknown>|null|undefined} legacy */
export function migrateFieldConfigToConstraints(legacy = {}) {
  const fc = legacy && typeof legacy === 'object' ? legacy : {};
  const behavior = String(fc.behavior || 'fitBox').toLowerCase();
  const mapped = LEGACY_BEHAVIOR_MAP[behavior] || LEGACY_BEHAVIOR_MAP.fitBox;

  return {
    layoutMode: fc.layoutMode || mapped.layoutMode,
    overflowStrategy: fc.overflowStrategy || mapped.overflowStrategy,
    minFontSize: Number(fc.minFontSize) > 0 ? Number(fc.minFontSize) : 6,
    maxFontSize: Number(fc.maxFontSize) > 0 ? Number(fc.maxFontSize) : 9999,
    padding: Number(fc.padding) >= 0 ? Number(fc.padding) : 2,
    anchor: fc.anchor || 'top-left',
    intendedLines: fc.intendedLines,
    legacyBehavior: behavior,
  };
}

/** Export constraints back into fieldConfig for Fabric persistence. */
export function constraintsToFieldConfig(frame, typography, constraints, extra = {}) {
  const pad = constraints.padding ?? 2;
  let behavior = 'fitBox';
  if (constraints.layoutMode === 'autoHeight' || constraints.overflowStrategy === 'wrap') {
    behavior = 'wrap';
  } else if (constraints.overflowStrategy === 'clip' && constraints.layoutMode === 'fixedFrame') {
    behavior = 'none';
  }
  return {
    ...extra,
    maxWidth: Math.max(1, (frame?.width || 100) - pad * 2),
    maxHeight: Math.max(1, (frame?.height || 40) - pad * 2),
    behavior,
    layoutMode: constraints.layoutMode,
    overflowStrategy: constraints.overflowStrategy,
    baseFontSize: typography?.fontSize || 12,
    minFontSize: constraints.minFontSize,
    maxFontSize: constraints.maxFontSize,
    anchor: constraints.anchor,
    padding: pad,
    textAlign: typography?.textAlign,
    lineHeight: typography?.lineHeight,
    charSpacing: typography?.letterSpacing,
    fontFamily: typography?.fontFamily,
    fontWeight: typography?.fontWeight,
    fontStyle: typography?.fontStyle,
    underline: Boolean(typography?.underline),
    fill: typography?.fill,
  };
}
